import {
  SystemConfig,
  FinancialParams,
  SolarData,
  EnergyProduction,
  FinancialMetrics,
  EnvironmentalImpact,
  CashFlowYear,
} from '../types';

/**
 * Calculate energy production based on solar data and system configuration
 */
export function calculateEnergyProduction(
  solarData: SolarData,
  systemConfig: SystemConfig
): EnergyProduction {
  const { capacity, panelType, inverterEfficiency, losses } = systemConfig;
  
  // Get panel efficiency factor based on panel type
  const panelEfficiencyFactor = getPanelEfficiencyFactor(panelType);
  
  // Calculate system efficiency (Performance Ratio)
  const systemEfficiency = calculateSystemEfficiency(inverterEfficiency, losses);
  const performanceRatio = systemEfficiency / 100;
  
  // Days per month (simplified)
  const daysPerMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  
  // Calculate monthly production
  const monthlyProduction = solarData.monthlyGHI.map((ghi, index) => {
    // Monthly production = GHI (kWh/m²/day) × Days × System Capacity (kWp) × Performance Ratio × Panel Efficiency Factor
    return ghi * daysPerMonth[index] * capacity * performanceRatio * panelEfficiencyFactor;
  });
  
  // Annual production
  const annualProduction = monthlyProduction.reduce((sum, val) => sum + val, 0);
  
  // Specific yield (kWh/kWp/year)
  const specificYield = annualProduction / capacity;
  
  // Lifetime production with degradation (FIXED: 25 years)
  const lifetimeProduction: number[] = [];
  const degradationRate = losses.degradation / 100;
  
  for (let year = 0; year < 25; year++) {
    const degradationFactor = Math.pow(1 - degradationRate, year);
    lifetimeProduction.push(annualProduction * degradationFactor);
  }
  
  return {
    monthlyProduction,
    annualProduction,
    lifetimeProduction,
    performanceRatio: performanceRatio * 100,
    specificYield,
  };
}

/**
 * Calculate overall system efficiency
 */
function calculateSystemEfficiency(
  inverterEfficiency: number,
  losses: {
    soiling: number;
    shading: number;
    dcLoss: number;
    acLoss: number;
    inverterLoss: number;
  }
): number {
  // Apply all loss factors
  let efficiency = 100;
  
  efficiency *= (100 - losses.soiling) / 100;
  efficiency *= (100 - losses.shading) / 100;
  efficiency *= (100 - losses.dcLoss) / 100;
  efficiency *= (100 - losses.acLoss) / 100;
  efficiency *= inverterEfficiency / 100;
  
  return efficiency;
}

/**
 * Calculate financial metrics
 */
export function calculateFinancialMetrics(
  energyProduction: EnergyProduction,
  financialParams: FinancialParams,
  systemCapacity: number
): FinancialMetrics {
  const { capex, opex, electricityPrice, electricityPriceEscalation, discountRate, projectLifetime } = financialParams;
  
  // Total CAPEX - panelCost is now total $ (not $/kWp), so no need to multiply
  const totalCapex = capex.panelCost + 
                     capex.inverterCost + 
                     capex.bosCost + 
                     capex.softCosts;
  
  // Total annual OPEX
  const annualOpex = Object.values(opex).reduce((sum, val) => sum + (val || 0), 0);
  
  // Generate cash flow for each year
  const cashFlow: CashFlowYear[] = [];
  let cumulativeCashFlow = -totalCapex; // Initial investment
  let totalRevenue = 0;
  let totalCost = totalCapex;
  
  for (let year = 1; year <= projectLifetime; year++) {
    const production = energyProduction.lifetimeProduction[year - 1] || 0;
    
    // Revenue with electricity price escalation
    const currentElectricityPrice = electricityPrice * Math.pow(1 + electricityPriceEscalation / 100, year - 1);
    const revenue = production * currentElectricityPrice;
    
    // Costs (OPEX only after initial investment)
    const costs = annualOpex;
    
    // Net cash flow
    const netCashFlow = revenue - costs;
    cumulativeCashFlow += netCashFlow;
    
    totalRevenue += revenue;
    totalCost += costs;
    
    cashFlow.push({
      year,
      production,
      revenue,
      costs,
      netCashFlow,
      cumulativeCashFlow,
    });
  }
  
  // Calculate NPV
  const npv = calculateNPV(cashFlow, totalCapex, discountRate);
  
  // Calculate IRR
  const irr = calculateIRR(cashFlow, totalCapex);
  
  // Calculate payback period
  const paybackPeriod = calculatePaybackPeriod(cashFlow);
  
  // Calculate LCOE - CRITICAL FIX: Only use production for projectLifetime years
  const lcoe = calculateLCOE(
    totalCapex, 
    annualOpex, 
    energyProduction.lifetimeProduction.slice(0, projectLifetime), // Only use projectLifetime years
    discountRate
  );
  
  console.log('[Financial Metrics] LCOE Calculation:', {
    totalCapex,
    annualOpex,
    projectLifetime,
    discountRate,
    firstYearProduction: energyProduction.lifetimeProduction[0],
    lcoe: lcoe.toFixed(4)
  });
  
  // Calculate ROI
  const netProfit = totalRevenue - totalCost;
  const roi = (netProfit / totalCapex) * 100;
  
  return {
    npv,
    irr,
    paybackPeriod,
    roi,
    lcoe,
    totalRevenue,
    totalCost,
    netProfit,
    cashFlow,
  };
}

/**
 * Calculate Net Present Value
 */
function calculateNPV(cashFlow: CashFlowYear[], initialInvestment: number, discountRate: number): number {
  let npv = -initialInvestment;
  
  cashFlow.forEach((cf) => {
    const discountFactor = Math.pow(1 + discountRate / 100, cf.year);
    npv += cf.netCashFlow / discountFactor;
  });
  
  return npv;
}

/**
 * Calculate Internal Rate of Return using Newton-Raphson method
 */
function calculateIRR(cashFlow: CashFlowYear[], initialInvestment: number): number {
  let irr = 0.1; // Initial guess 10%
  const maxIterations = 100;
  const tolerance = 0.0001;
  
  for (let i = 0; i < maxIterations; i++) {
    let npv = -initialInvestment;
    let derivative = 0;
    
    cashFlow.forEach((cf) => {
      const factor = Math.pow(1 + irr, cf.year);
      npv += cf.netCashFlow / factor;
      derivative -= (cf.year * cf.netCashFlow) / (factor * (1 + irr));
    });
    
    const newIrr = irr - npv / derivative;
    
    if (Math.abs(newIrr - irr) < tolerance) {
      return newIrr * 100; // Return as percentage
    }
    
    irr = newIrr;
  }
  
  return irr * 100; // Return as percentage
}

/**
 * Calculate simple payback period
 */
function calculatePaybackPeriod(cashFlow: CashFlowYear[]): number {
  for (let i = 0; i < cashFlow.length; i++) {
    if (cashFlow[i].cumulativeCashFlow >= 0) {
      // Interpolate to get more precise payback period
      if (i > 0) {
        const prevCumulative = cashFlow[i - 1].cumulativeCashFlow;
        const currentCumulative = cashFlow[i].cumulativeCashFlow;
        const fraction = Math.abs(prevCumulative) / (currentCumulative - prevCumulative);
        return i + fraction;
      }
      return i + 1;
    }
  }
  
  return cashFlow.length; // Not paid back within project lifetime
}

/**
 * Calculate Levelized Cost of Energy
 */
function calculateLCOE(
  initialInvestment: number,
  annualOpex: number,
  lifetimeProduction: number[],
  discountRate: number
): number {
  let totalCostPV = initialInvestment;
  let totalEnergyPV = 0;
  
  lifetimeProduction.forEach((production, index) => {
    const year = index + 1;
    const discountFactor = Math.pow(1 + discountRate / 100, year);
    
    totalCostPV += annualOpex / discountFactor;
    totalEnergyPV += production / discountFactor;
  });
  
  // Safety check: prevent division by zero
  if (totalEnergyPV === 0 || !isFinite(totalEnergyPV)) {
    console.error('LCOE Calculation Error: totalEnergyPV is zero or invalid', {
      totalEnergyPV,
      lifetimeProduction: lifetimeProduction.slice(0, 5),
      discountRate
    });
    return 0; // Return 0 instead of Infinity/NaN
  }
  
  const lcoe = totalCostPV / totalEnergyPV;
  
  // Additional safety check for the result
  if (!isFinite(lcoe) || lcoe < 0) {
    console.error('LCOE Calculation Error: Invalid result', {
      lcoe,
      totalCostPV,
      totalEnergyPV
    });
    return 0;
  }
  
  return lcoe;
}

/**
 * Calculate environmental impact
 */
export function calculateEnvironmentalImpact(
  energyProduction: EnergyProduction
): EnvironmentalImpact {
  // Total energy production over lifetime
  const totalProduction = energyProduction.lifetimeProduction.reduce((sum, val) => sum + val, 0);
  
  // CO2 avoided (kg CO2 per kWh) - global average for electricity
  const co2Factor = 0.475; // kg CO2 per kWh
  const co2Avoided = (totalProduction * co2Factor) / 1000; // Convert to tons
  
  // Trees equivalent (one tree absorbs ~21 kg CO2 per year)
  const treesEquivalent = Math.round((co2Avoided * 1000) / (21 * 25));
  
  // Coal avoided (1 ton of coal = ~2.86 tons CO2)
  const coalAvoided = co2Avoided / 2.86;
  
  return {
    co2Avoided,
    treesEquivalent,
    coalAvoided,
  };
}

/**
 * Get panel efficiency factor based on type
 */
export function getPanelEfficiencyFactor(type: string): number {
  switch (type) {
    case 'monocrystalline':
      return 1.0; // Baseline - 100% (highest efficiency)
    case 'polycrystalline':
      return 0.85; // 15% less efficient than monocrystalline
    case 'thin-film':
      return 0.60; // 40% less efficient than monocrystalline
    default:
      return 0.85;
  }
}

/**
 * Get recommended inverter efficiency based on system type
 */
export function getInverterEfficiency(systemType: string): number {
  switch (systemType) {
    case 'grid-tied':
      return 97; // Highest efficiency - direct grid connection
    case 'hybrid':
      return 95; // Medium efficiency - battery + grid
    case 'off-grid':
      return 93; // Lower efficiency - battery dependent
    default:
      return 96;
  }
}

/**
 * Get default system losses
 */
export function getDefaultLosses() {
  return {
    soiling: 2,
    shading: 3,
    dcLoss: 2,
    acLoss: 1,
    inverterLoss: 3,
    degradation: 0.5,
  };
}

/**
 * Get industry benchmark ranges for each loss type
 */
export function getIndustryBenchmark(lossType: string): { min: number; max: number; optimal: number } {
  const benchmarks: Record<string, { min: number; max: number; optimal: number }> = {
    soiling: { min: 0.5, max: 8.0, optimal: 2.0 },
    shading: { min: 0.5, max: 10.0, optimal: 3.0 },
    dcLoss: { min: 1.0, max: 5.0, optimal: 2.0 },
    acLoss: { min: 0.5, max: 3.0, optimal: 1.0 },
    inverterLoss: { min: 1.0, max: 5.0, optimal: 3.0 },
    degradation: { min: 0.3, max: 1.5, optimal: 0.5 },
  };
  
  return benchmarks[lossType] || { min: 0, max: 10, optimal: 2 };
}

/**
 * Get status based on value compared to industry benchmark
 */
export function getLossStatus(lossType: string, value: number): 'excellent' | 'good' | 'acceptable' | 'high' {
  const benchmark = getIndustryBenchmark(lossType);
  
  if (value <= benchmark.optimal) {
    return 'excellent';
  } else if (value <= benchmark.optimal + (benchmark.max - benchmark.optimal) * 0.3) {
    return 'good';
  } else if (value <= benchmark.optimal + (benchmark.max - benchmark.optimal) * 0.6) {
    return 'acceptable';
  } else {
    return 'high';
  }
}

/**
 * Detect climate zone based on latitude and longitude
 */
export function getClimateZone(lat: number, lon: number): 'desert' | 'tropical' | 'temperate' | 'cold' {
  const absLat = Math.abs(lat);
  
  // Tropical zone: -23.5° to +23.5° latitude (Tropic of Cancer/Capricorn)
  // Expanded from -10/+10 to include Mumbai, Bangkok, Havana etc.
  if (absLat <= 23.5) {
    return 'tropical';
  }
  
  // Desert/Arid zone: 15° to 35° latitude (hot deserts)
  // Common regions: Middle East, Sahara, SW USA, Australian Outback, Thar Desert
  if (absLat >= 15 && absLat <= 35) {
    // Additional longitude checks for major desert regions
    const isMiddleEastSouthAsia = lon >= 30 && lon <= 80; // Middle East + Thar Desert (expanded from 70 to 80)
    const isNorthAfrica = lon >= -15 && lon <= 35 && lat >= 15 && lat <= 35; // Sahara
    const isSWUSA = lon >= -120 && lon <= -100 && lat >= 25 && lat <= 40; // SW USA
    const isAustralia = lon >= 115 && lon <= 145 && lat >= -35 && lat <= -15; // Australian Outback
    
    if (isMiddleEastSouthAsia || isNorthAfrica || isSWUSA || isAustralia) {
      return 'desert';
    }
    
    // Otherwise temperate (Mediterranean, subtropics)
    return 'temperate';
  }
  
  // Cold zone: >50° latitude
  if (absLat > 50) {
    return 'cold';
  }
  
  // Default: Temperate zone (35° to 50°)
  return 'temperate';
}

/**
 * Estimate temperature based on latitude (fallback when avgTemp not available)
 */
function estimateTemperature(lat: number): number {
  const absLat = Math.abs(lat);
  
  if (absLat < 15) return 28; // Equatorial
  if (absLat < 30) return 25; // Subtropical
  if (absLat < 45) return 18; // Temperate
  if (absLat < 60) return 10; // Cool temperate
  return 3; // Cold/Arctic
}

/**
 * Auto-detect optimal system losses based on location, system configuration, and industry standards
 * Uses NREL PVWatts methodology and IEC 61724 standards
 * 
 * Enhanced with NASA POWER climate data (temperature, precipitation, humidity)
 * for more accurate soiling and degradation modeling
 */
export function autoDetectLosses(
  latitude: number,
  longitude: number,
  systemType: string,
  panelType: string,
  capacity: number,
  avgTemp?: number,
  avgPrecip?: number,
  avgHumidity?: number
): {
  soiling: number;
  shading: number;
  dcLoss: number;
  acLoss: number;
  inverterLoss: number;
  degradation: number;
} {
  console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('🎯 AUTO DETECT LOSSES CALCULATION');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━���━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('📍 Location:', `${latitude.toFixed(4)}°, ${longitude.toFixed(4)}°`);
  console.log('🔧 System Config:', { systemType, panelType, capacity: `${capacity} kW` });
  
  // Apply fallbacks for optional climate parameters
  const temperature = avgTemp !== undefined ? avgTemp : estimateTemperature(latitude);
  const precipitation = avgPrecip !== undefined ? avgPrecip : 2.0; // mm/day global average
  const humidity = avgHumidity !== undefined ? avgHumidity : 60; // % global average
  
  console.log('\n🌤️  CLIMATE DATA:');
  console.log('   🌡️  Temperature:', temperature.toFixed(2), '°C', avgTemp !== undefined ? '(NASA)' : '(estimated)');
  console.log('   💧 Precipitation:', precipitation.toFixed(2), 'mm/day', avgPrecip !== undefined ? '(NASA)' : '(fallback)');
  console.log('   💨 Humidity:', humidity.toFixed(1), '%', avgHumidity !== undefined ? '(NASA)' : '(fallback)');
  
  // STEP 1: Base values (NREL PVWatts industry standards)
  let soiling = 2.0;
  let shading = 3.0;
  let dcLoss = 2.0;
  let acLoss = 1.0;
  let inverterLoss = 3.0;
  let degradation = 0.5;
  
  console.log('\n📊 BASE VALUES (NREL PVWatts):');
  console.log('   Soiling:', soiling, '% | Shading:', shading, '% | DC Loss:', dcLoss, '%');
  console.log('   AC Loss:', acLoss, '% | Inverter:', inverterLoss, '% | Degradation:', degradation, '%/year');

  // STEP 2: Climate zone detection (REVISED - expanded tropical range)
  const climateZone = getClimateZone(latitude, longitude);
  console.log('\n🌍 CLIMATE ZONE:', climateZone.toUpperCase());
  
  // STEP 3: Three-mechanism soiling model
  console.log('\n🧪 THREE-MECHANISM SOILING MODEL:');
  
  // Mechanism A: Dust/particulate (driven by climate zone)
  let dustSoiling = 0.0;
  switch (climateZone) {
    case 'desert':
      dustSoiling = 2.0; // Heavy dust accumulation
      console.log('   ⛰️  Dust Soiling (Desert):', '+' + dustSoiling.toFixed(1), '%');
      break;
    case 'cold':
      dustSoiling = -0.5; // Less dust, but snow accumulation
      console.log('   ❄️  Dust Soiling (Cold):', dustSoiling.toFixed(1), '%');
      break;
    case 'tropical':
    case 'temperate':
      dustSoiling = 0.0; // Neutral - other mechanisms dominate
      console.log('   🌿 Dust Soiling (Neutral):', dustSoiling.toFixed(1), '%');
      break;
  }
  
  // Mechanism B: Biological soiling (driven by humidity × temperature)
  // Calibrated thresholds based on Mumbai, Kolkata, Ivory Coast data
  let bioSoiling = 0.0;
  if (humidity > 68 && temperature > 24) {
    bioSoiling = 1.5; // High biological activity (algae, fungi, organic buildup)
    console.log('   🦠 Biological Soiling (High - Algae/Fungi):', '+' + bioSoiling.toFixed(1), '%');
    console.log('      └─ Trigger: Humidity > 68% AND Temperature > 24°C');
  } else if (humidity > 60 && temperature > 18) {
    bioSoiling = 0.5; // Moderate biological activity
    console.log('   🦠 Biological Soiling (Moderate):', '+' + bioSoiling.toFixed(1), '%');
    console.log('      └─ Trigger: Humidity > 60% AND Temperature > 18°C');
  } else {
    console.log('   🦠 Biological Soiling:', bioSoiling.toFixed(1), '% (conditions not met)');
  }
  
  // Mechanism C: Rainfall cleaning (driven by precipitation)
  // Calibrated to account for seasonal rain patterns
  let cleaning = 0.0;
  if (precipitation > 5.0) {
    cleaning = -1.0; // Very high rainfall (monsoon regions)
    console.log('   🌧️  Rainfall Cleaning (Monsoon):', cleaning.toFixed(1), '%');
  } else if (precipitation > 3.0) {
    cleaning = -0.7; // High rainfall
    console.log('   🌧️  Rainfall Cleaning (High):', cleaning.toFixed(1), '%');
  } else if (precipitation > 1.5) {
    cleaning = -0.4; // Moderate rainfall
    console.log('   🌧️  Rainfall Cleaning (Moderate):', cleaning.toFixed(1), '%');
  } else {
    console.log('   🌧️  Rainfall Cleaning:', cleaning.toFixed(1), '% (dry climate)');
  }
  // precipitation <= 1.5: no cleaning benefit
  
  // Combine all three mechanisms
  const oldSoiling = soiling;
  soiling = soiling + dustSoiling + bioSoiling + cleaning;
  console.log('\n   ✅ SOILING CALCULATION:');
  console.log('      Base:', oldSoiling, '+ Dust:', dustSoiling, '+ Bio:', bioSoiling, '+ Clean:', cleaning);
  console.log('      = Total:', soiling.toFixed(1), '%');
  
  // Tropical zones: additional vegetation-based shading
  if (climateZone === 'tropical') {
    shading += 0.5;
    console.log('\n🌴 Tropical Shading Adjustment: +0.5% (vegetation growth)');
  }
  
  // Cold zones: longer shadows from low sun angles
  if (climateZone === 'cold') {
    shading += 1.0;
    console.log('\n❄️  Cold Zone Shading Adjustment: +1.0% (low sun angles)');
  }

  // STEP 4: System type adjustments
  console.log('\n⚙️  SYSTEM TYPE ADJUSTMENTS (' + systemType + '):');
  switch (systemType) {
    case 'grid-tied':
      // Professional installation, optimal site selection
      shading -= 1.0;
      dcLoss -= 0.5;
      console.log('   Shading: -1.0% (professional site analysis)');
      console.log('   DC Loss: -0.5% (optimized cable runs)');
      break;
    case 'off-grid':
      // Remote locations, suboptimal conditions
      shading += 1.0;
      dcLoss += 1.0;
      console.log('   Shading: +1.0% (remote location constraints)');
      console.log('   DC Loss: +1.0% (battery losses, longer cables)');
      break;
    case 'hybrid':
      // Battery integration adds DC losses
      dcLoss += 0.5;
      console.log('   DC Loss: +0.5% (battery integration)');
      break;
  }

  // STEP 5: Panel type base degradation rates (temperature-sensitive)
  console.log('\n🔬 DEGRADATION CALCULATION (' + panelType + '):');
  switch (panelType) {
    case 'monocrystalline':
      degradation = 0.35; // Premium panels
      console.log('   Base Rate: 0.35%/year (premium panels)');
      break;
    case 'polycrystalline':
      degradation = 0.45; // Standard panels
      console.log('   Base Rate: 0.45%/year (standard panels)');
      break;
    case 'thin-film':
      degradation = 0.75; // Higher degradation
      console.log('   Base Rate: 0.75%/year (thin-film)');
      break;
  }
  
  // Temperature-based degradation adjustment (Arrhenius-inspired)
  if (temperature > 25) {
    // Each degree above 25°C adds thermal stress
    const tempAdjustment = (temperature - 25) * 0.01;
    degradation += tempAdjustment;
    console.log('   Temperature Adjustment: +' + tempAdjustment.toFixed(3), '%/year (temp > 25°C)');
  }
  
  if (temperature < 10) {
    // Cold climates: reduced thermal stress
    degradation -= 0.05;
    console.log('   Cold Climate Adjustment: -0.05%/year (reduced thermal stress)');
  }
  
  // Humidity-based degradation adjustment (lamination stress)
  if (humidity > 75) {
    degradation += 0.05;
    console.log('   Humidity Adjustment: +0.05%/year (lamination stress, humidity > 75%)');
  }
  
  console.log('   Final Degradation:', degradation.toFixed(2), '%/year');

  // STEP 6: System size adjustments
  console.log('\n📏 SYSTEM SIZE ADJUSTMENTS (' + capacity + ' kW):');
  if (capacity >= 500) {
    // Large commercial: professional O&M
    soiling -= 0.5;
    shading -= 0.5;
    console.log('   Large Commercial (≥500 kW):');
    console.log('   └─ Soiling: -0.5% (regular maintenance)');
    console.log('   └─ Shading: -0.5% (professional design)');
  } else if (capacity < 50) {
    // Small residential: less frequent maintenance
    soiling += 0.5;
    shading += 0.5;
    console.log('   Small Residential (<50 kW):');
    console.log('   └─ Soiling: +0.5% (less frequent cleaning)');
    console.log('   └─ Shading: +0.5% (roof constraints)');
  } else {
    console.log('   Medium System (50-500 kW): No adjustment');
  }

  // STEP 7: Bounds enforcement
  const beforeBounds = { soiling, shading, dcLoss, acLoss, inverterLoss, degradation };
  soiling = Math.max(0.5, Math.min(8.0, soiling));
  shading = Math.max(0.5, Math.min(10.0, shading));
  dcLoss = Math.max(1.0, Math.min(5.0, dcLoss));
  acLoss = Math.max(0.5, Math.min(3.0, acLoss));
  inverterLoss = Math.max(1.0, Math.min(5.0, inverterLoss));
  degradation = Math.max(0.3, Math.min(1.5, degradation));
  
  console.log('\n🔒 BOUNDS ENFORCEMENT:');
  console.log('   Soiling:', beforeBounds.soiling.toFixed(2), '→', soiling.toFixed(1), '% (0.5-8.0)');
  console.log('   Shading:', beforeBounds.shading.toFixed(2), '→', shading.toFixed(1), '% (0.5-10.0)');
  console.log('   DC Loss:', beforeBounds.dcLoss.toFixed(2), '→', dcLoss.toFixed(1), '% (1.0-5.0)');
  console.log('   AC Loss:', beforeBounds.acLoss.toFixed(2), '→', acLoss.toFixed(1), '% (0.5-3.0)');
  console.log('   Inverter:', beforeBounds.inverterLoss.toFixed(2), '→', inverterLoss.toFixed(1), '% (1.0-5.0)');
  console.log('   Degradation:', beforeBounds.degradation.toFixed(3), '→', degradation.toFixed(2), '%/year (0.3-1.5)');

  // Round to 1 decimal place
  const result = {
    soiling: Math.round(soiling * 10) / 10,
    shading: Math.round(shading * 10) / 10,
    dcLoss: Math.round(dcLoss * 10) / 10,
    acLoss: Math.round(acLoss * 10) / 10,
    inverterLoss: Math.round(inverterLoss * 10) / 10,
    degradation: Math.round(degradation * 10) / 10,
  };
  
  console.log('\n✅ FINAL DETECTED LOSSES:');
  console.log('   Soiling:', result.soiling, '%');
  console.log('   Shading:', result.shading, '%');
  console.log('   DC Loss:', result.dcLoss, '%');
  console.log('   AC Loss:', result.acLoss, '%');
  console.log('   Inverter Loss:', result.inverterLoss, '%');
  console.log('   Degradation:', result.degradation, '%/year');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
  
  return result;
}